import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { useChats, storeKeys } from 'hooks/api/useStore';
import { generatedApiClient } from 'services/generatedApiClient';
import type { StoreMessage } from 'generated/api';
import styles from '../styles/pages/HistoryPage.module.css';

interface HistoryPageProps {
  className?: string;
}

const HistoryPageContent: React.FC<HistoryPageProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedNamespace, setSelectedNamespace] = useState('frontend');

  // Fetch chats for the selected namespace
  const {
    data: chatIds = [],
    isLoading: chatsLoading,
    error: chatsError,
    refetch,
  } = useChats(selectedNamespace);

  // Use React Query's useQueries to efficiently fetch chat histories with proper caching
  const chatHistoryQueries = useQueries({
    queries: chatIds.map(chatId => ({
      queryKey: storeKeys.chatHistory(selectedNamespace, chatId),
      queryFn: async () => {
        const response = await generatedApiClient.getChatHistory(
          selectedNamespace,
          chatId
        );
        if (response.error) {
          throw new Error(response.error);
        }
        return response.data || [];
      },
      // Aggressive caching for chat histories since they don't change often
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
      retry: 1, // Only retry once to avoid overloading
      enabled: !!chatId && !!selectedNamespace, // Only fetch if we have valid params
    })),
  });

  // Fetch unique models for each chat
  const uniqueModelsQueries = useQueries({
    queries: chatIds.map(chatId => ({
      queryKey: storeKeys.chatUniqueModels(selectedNamespace, chatId),
      queryFn: async () => {
        const response = await generatedApiClient.getChatUniqueModels(
          selectedNamespace,
          chatId
        );
        // Debug: log unique models per chat for troubleshooting "Unknown Model"
        if (response?.data) {
          console.log(
            '[HistoryPage] Unique models for chat',
            chatId,
            response.data
          );
        } else if (response?.error) {
          console.warn(
            '[HistoryPage] Failed to fetch unique models for chat',
            chatId,
            response.error
          );
        }
        if (response.error) {
          throw new Error(response.error);
        }
        return response.data || [];
      },
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 1,
      enabled: !!chatId && !!selectedNamespace,
    })),
  });

  // Compute loading and error states from React Query results
  const isLoadingHistories =
    chatHistoryQueries.some(query => query.isLoading) ||
    uniqueModelsQueries.some(q => q.isLoading);
  const hasHistoryErrors =
    chatHistoryQueries.some(query => query.isError) ||
    uniqueModelsQueries.some(q => q.isError);

  // Helper function to extract meaningful title from messages
  const extractChatTitle = useCallback((messages: StoreMessage[]): string => {
    if (!messages.length) return 'Empty Chat';

    // Find the first user message
    const firstUserMessage = messages.find(msg => {
      const role = (msg.message as any)?.role;
      return role === 'user';
    });

    if (firstUserMessage) {
      const content = extractMessageContent(firstUserMessage);
      // Take first 50 characters and add ellipsis if longer
      return content.length > 50 ? content.substring(0, 50) + '...' : content;
    }

    return `Chat ${messages.length} messages`;
  }, []);

  // Helper function to extract content from message
  const extractMessageContent = useCallback((message: StoreMessage): string => {
    if (!message.message) return '';

    const msgObj = message.message as any;

    // Handle different message formats - prioritize 'content' field
    if (msgObj.content) {
      // Direct content field (standard format)
      return msgObj.content;
    } else if (msgObj.text) {
      // Legacy text field (old format)
      return msgObj.text;
    } else if (
      msgObj.parts &&
      Array.isArray(msgObj.parts) &&
      msgObj.parts.length > 0
    ) {
      // Parts array format
      const firstPart = msgObj.parts[0];
      if (typeof firstPart === 'string') return firstPart;
      if (firstPart.text) return firstPart.text;
      if (firstPart.content) return firstPart.content;
    }

    // If we can't find content, return a fallback instead of JSON
    return 'No content available';
  }, []);

  // Helper function to extract model information
  const extractModelInfo = useCallback(
    (messages: StoreMessage[]): { model: string; agent: string } => {
      // First, check if any message has model info stored directly
      const messageWithModel = messages.find(msg => {
        const msgObj = msg.message as any;
        return msgObj?.model;
      });

      if (messageWithModel) {
        const model = (messageWithModel.message as any)?.model;
        return { model: model, agent: 'AI Assistant' };
      }

      // Fallback: Look for system messages or assistant messages that might contain model info
      const systemMessage = messages.find(msg => {
        const role = (msg.message as any)?.role;
        return role === 'system' || role === 'assistant';
      });

      if (systemMessage) {
        const content = extractMessageContent(systemMessage).toLowerCase();

        // Try to extract model name from content
        if (content.includes('gpt-4'))
          return { model: 'GPT-4', agent: 'OpenAI' };
        if (content.includes('gpt-3'))
          return { model: 'GPT-3.5', agent: 'OpenAI' };
        if (content.includes('gemma'))
          return { model: 'Gemma', agent: 'Google' };
        if (content.includes('llama')) return { model: 'Llama', agent: 'Meta' };
        if (content.includes('claude'))
          return { model: 'Claude', agent: 'Anthropic' };
      }

      return { model: 'Unknown Model', agent: 'AI Assistant' };
    },
    [extractMessageContent]
  );

  // Convert chat IDs to enhanced chat items with real data from React Query
  const chatDetails = useMemo(() => {
    return chatIds.map((chatId, index) => {
      const queryResult = chatHistoryQueries[index];
      const messages = queryResult?.data || [];
      const isLoading = queryResult?.isLoading ?? true;
      const hasError = queryResult?.isError ?? false;

      const modelsResult = uniqueModelsQueries[index];
      const uniqueModels = (modelsResult?.data as any[]) || [];

      if (isLoading || hasError || !messages.length) {
        return {
          id: chatId,
          title: isLoading
            ? 'Loading...'
            : hasError
              ? 'Error loading chat'
              : 'Empty Chat',
          description: isLoading
            ? 'Loading chat history...'
            : hasError
              ? 'Failed to load messages'
              : 'No messages in this chat',
          timestamp: new Date(),
          agent: 'Unknown',
          type: 'Text',
          model: 'Unknown',
          tags: isLoading ? ['Loading'] : hasError ? ['Error'] : ['Empty'],
          messageCount: 0,
          lastMessage: '',
        };
      }

      const title = extractChatTitle(messages);
      const { model, agent } = extractModelInfo(messages);

      // If backend provided unique models, prefer those for the card tags
      const uniqueModelNames = Array.isArray(uniqueModels)
        ? uniqueModels
            .map((m: any) => m?.name || m?.hfid || m?.id)
            .filter(Boolean)
        : [];

      // Prefer backend-provided unique model names for display if available
      const displayModel =
        uniqueModelNames.length > 0 ? String(uniqueModelNames[0]) : model;

      // Get the last message for description
      const lastMessage =
        messages.length > 0 ? messages[messages.length - 1] : null;
      const description = lastMessage
        ? extractMessageContent(lastMessage)
        : 'No messages';

      // Get timestamp from the last message
      const timestamp =
        lastMessage?.updated_at || lastMessage?.created_at
          ? new Date(lastMessage.updated_at || lastMessage.created_at!)
          : new Date();

      const baseTags =
        uniqueModelNames.length > 0
          ? [agent, 'Text', ...uniqueModelNames]
          : [agent, 'Text', displayModel];
      const dedupedTags = baseTags.filter((v, i, arr) => arr.indexOf(v) === i);

      // Debug when model is unknown and backend did not return any unique models
      if (
        !uniqueModelNames.length &&
        (!displayModel || displayModel.toLowerCase().includes('unknown'))
      ) {
        console.warn('[HistoryPage] Model unknown for chat', chatId, {
          agent,
          extractedModel: model,
          uniqueModelsResponse: uniqueModels,
          lastMessageId: lastMessage?.id,
        });
      }

      return {
        id: chatId,
        title,
        description:
          description.length > 150
            ? description.substring(0, 150) + '...'
            : description,
        timestamp,
        agent,
        type: 'Text', // Default to text for now
        model: displayModel,
        tags: dedupedTags,
        messageCount: messages.length,
        lastMessage: description,
      };
    });
  }, [
    chatIds,
    chatHistoryQueries,
    extractChatTitle,
    extractModelInfo,
    extractMessageContent,
    uniqueModelsQueries,
  ]);

  // Get unique values for filter options
  const filterOptions = useMemo(() => {
    const agents = new Set<string>();
    const types = new Set<string>();
    const models = new Set<string>();

    chatDetails.forEach(chat => {
      if (chat.agent && chat.agent !== 'Unknown') agents.add(chat.agent);
      if (chat.type) types.add(chat.type);
      if (chat.model && chat.model !== 'Unknown') models.add(chat.model);
    });

    return {
      agents: Array.from(agents).sort(),
      types: Array.from(types).sort(),
      models: Array.from(models).sort(),
    };
  }, [chatDetails]);

  // Filter chats based on selected filters
  const filteredChats = useMemo(() => {
    return chatDetails.filter(chat => {
      // Always show loading, error, and empty chats
      if (
        chat.tags.includes('Loading') ||
        chat.tags.includes('Error') ||
        chat.tags.includes('Empty')
      ) {
        return true;
      }

      if (selectedAgent && chat.agent !== selectedAgent) return false;
      if (selectedType && chat.type !== selectedType) return false;
      if (selectedModel && chat.model !== selectedModel) return false;
      if (selectedDate) {
        const chatDate = chat.timestamp.toISOString().split('T')[0];
        if (chatDate !== selectedDate) return false;
      }
      return true;
    });
  }, [chatDetails, selectedAgent, selectedType, selectedModel, selectedDate]);

  // Auto-scroll the history list to the bottom (newest) when data updates
  const listRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (listRef.current) {
      try {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      } catch (_) {
        // Fallback to window scroll if container scrolling fails
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: document.body.scrollHeight });
        }
      }
    }
  }, [filteredChats.length]);

  // Handle chat click - navigate to text chat with the specific chat loaded
  const handleChatClick = useCallback(
    (chatId: string) => {
      navigate(`/text-chat?chatId=${chatId}&namespace=${selectedNamespace}`);
    },
    [navigate, selectedNamespace]
  );

  // Format timestamp for display
  const formatTimestamp = useCallback((timestamp: Date) => {
    return timestamp.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  }, []);

  if (chatsError) {
    return (
      <div className={`${styles.historyPage} ${className}`}>
        <div className={styles.errorState}>
          <h2>Failed to load chats</h2>
          <p>There was an error loading your chat history.</p>
          <button onClick={() => refetch()}>Try Again</button>
        </div>
      </div>
    );
  }

  const headerSlot =
    typeof document !== 'undefined'
      ? document.getElementById('topbar-slot')
      : null;

  const Filters = (
    <div className={styles.topNavInner}>
      <div className={styles.dropdownFilters}>
        <div className={styles.dropdown}>
          <select
            value={selectedNamespace}
            onChange={e => setSelectedNamespace(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="frontend">Frontend</option>
            <option value="models-gallery">Models Gallery</option>
          </select>
        </div>

        <div className={styles.dropdown}>
          <select
            value={selectedAgent}
            onChange={e => setSelectedAgent(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">Agent</option>
            {filterOptions.agents.map(agent => (
              <option key={agent} value={agent}>
                {agent}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.dropdown}>
          <select
            value={selectedType}
            onChange={e => setSelectedType(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">Type</option>
            {filterOptions.types.map(type => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.dropdown}>
          <select
            value={selectedModel}
            onChange={e => setSelectedModel(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">Model</option>
            {filterOptions.models.map(model => (
              <option key={model} value={model}>
                {model}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.inputField}>
          <input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            className={styles.dateInput}
            placeholder="DD/MM/YY"
          />
        </div>

        <button
          onClick={() => refetch()}
          disabled={chatsLoading || isLoadingHistories}
          className={styles.refreshButton}
          title="Refresh chat list"
        >
          {chatsLoading || isLoadingHistories ? '⏳' : '🔄'}
        </button>

        {isLoadingHistories && !chatsLoading && (
          <div className={styles.loadingIndicator}>Loading chat details...</div>
        )}
      </div>
      <div aria-hidden className={styles.rightSpacer} />
    </div>
  );

  return (
    <div className={`${styles.historyPage} ${className}`}>
      {headerSlot ? (
        createPortal(Filters, headerSlot)
      ) : (
        <div className={styles.topNav}>{Filters}</div>
      )}

      <div className={styles.content}>
        {chatsLoading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner}></div>
            <p>Loading chats...</p>
          </div>
        ) : filteredChats.length === 0 ? (
          <div className={styles.emptyState}>
            <h3>No chats found</h3>
            <p>
              {chatIds.length === 0
                ? 'Start a new conversation to see it here.'
                : 'No chats match your current filters. Try adjusting the filters above.'}
            </p>
          </div>
        ) : (
          <div className={styles.historyList} ref={listRef}>
            {filteredChats.map(chat => (
              <div key={chat.id} className={styles.historyCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.dateTag}>
                    <span className={styles.dateText}>
                      {formatTimestamp(chat.timestamp)}
                    </span>
                  </div>
                  <h2 className={styles.cardTitle}>{chat.title}</h2>
                </div>

                <p className={styles.cardDescription}>{chat.description}</p>

                <div className={styles.divider}></div>

                <div className={styles.cardFooter}>
                  <div className={styles.tagGroup}>
                    {chat.tags
                      .filter(
                        tag => !['Loading', 'Error', 'Empty'].includes(tag)
                      )
                      .map((tag, index) => (
                        <div key={index} className={styles.tag}>
                          <span className={styles.tagText}>{tag}</span>
                        </div>
                      ))}
                    {chat.messageCount > 0 && (
                      <div className={styles.tag}>
                        <span className={styles.tagText}>
                          {chat.messageCount} messages
                        </span>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => handleChatClick(chat.id)}
                    className={styles.readButton}
                    disabled={
                      chat.tags.includes('Loading') ||
                      chat.tags.includes('Error')
                    }
                  >
                    <span className={styles.buttonText}>
                      {chat.tags.includes('Loading')
                        ? 'Loading...'
                        : chat.tags.includes('Error')
                          ? 'Error'
                          : 'Read the history'}
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const HistoryPage: React.FC<HistoryPageProps> = props => {
  return <HistoryPageContent {...props} />;
};

export default HistoryPage;
